import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Trash2, Mail, MailOpen, Download, Building2, Phone, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCIPLINE_LABELS = {
  process: "Prozessoptimierung",
  supply_chain: "Defense & Security Markt",
  industrial_ai: "Supply Chain Resilience & Nearshoring",
  ai_robotics: "Industrial AI & Robotics",
  other: "Sonstiges",
};

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status !== "all") params.set("status", status);
      const res = await axios.get(`${API}/admin/contacts?${params}`, { withCredentials: true });
      setItems(res.data);
    } catch {
      toast.error("Fehler beim Laden der Anfragen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setRead = async (id, read) => {
    try {
      const res = await axios.patch(`${API}/admin/contacts/${id}`, { read }, { withCredentials: true });
      setItems((prev) => prev.map((c) => (c.id === id ? res.data : c)));
      if (active?.id === id) setActive(res.data);
    } catch {
      toast.error("Aktualisierung fehlgeschlagen.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Diese Anfrage wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((c) => c.id !== id));
      if (active?.id === id) setActive(null);
      toast.success("Anfrage gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const exportCsv = () => {
    window.open(`${API}/admin/contacts/export.csv`, "_blank");
  };

  return (
    <div data-testid="admin-contacts-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1E3A8A]">Inbox</div>
          <h1 className="mt-3 font-heading font-bold text-[#0A192F] text-3xl lg:text-4xl tracking-tight">Kontaktanfragen</h1>
          <p className="mt-2 text-slate-600">{items.length} Einträge</p>
        </div>
        <button
          onClick={exportCsv}
          data-testid="export-csv"
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-300 rounded-full text-sm font-semibold text-slate-700 hover:bg-white"
        >
          <Download size={16} /> CSV exportieren
        </button>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Name, E-Mail, Unternehmen…"
            data-testid="contacts-search"
            className="w-full pl-10 pr-3 py-2.5 rounded-full bg-white border border-slate-200 text-sm focus:border-[#0A192F] focus:outline-none"
          />
        </div>
        <div className="flex gap-2 bg-white rounded-full border border-slate-200 p-1" data-testid="contacts-status-filter">
          {[
            { v: "all", l: "Alle" },
            { v: "unread", l: "Ungelesen" },
            { v: "read", l: "Gelesen" },
          ].map((o) => (
            <button
              key={o.v}
              data-testid={`status-${o.v}`}
              onClick={() => setStatus(o.v)}
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] font-semibold ${
                status === o.v ? "bg-[#0A192F] text-white" : "text-slate-600 hover:text-[#0A192F]"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
        <button onClick={load} className="px-4 py-2.5 bg-[#0A192F] text-white rounded-full text-sm font-semibold hover:bg-[#1E3A8A]">Filter anwenden</button>
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500">Lade…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="contacts-empty">Keine Anfragen gefunden.</div>
          ) : (
            <ul className="divide-y divide-slate-200 max-h-[640px] overflow-y-auto">
              {items.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => { setActive(c); if (!c.read) setRead(c.id, true); }}
                    data-testid={`contact-row-${c.id}`}
                    className={`w-full text-left p-5 hover:bg-slate-50 transition-colors flex items-start gap-3 ${
                      active?.id === c.id ? "bg-slate-50" : ""
                    }`}
                  >
                    <div className="mt-1">
                      {c.read ? (
                        <MailOpen size={16} className="text-slate-400" />
                      ) : (
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#3B82F6]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className={`truncate ${c.read ? "text-slate-700" : "text-[#0A192F] font-semibold"}`}>{c.name}</div>
                        <div className="text-xs text-slate-400 flex-shrink-0">
                          {new Date(c.created_at).toLocaleDateString("de-DE")}
                        </div>
                      </div>
                      <div className="text-sm text-slate-500 truncate">{c.email}</div>
                      {c.discipline && DISCIPLINE_LABELS[c.discipline] && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#1E3A8A] bg-[#1E3A8A]/10 px-2 py-0.5 rounded-full">
                          <Tag size={10} /> {DISCIPLINE_LABELS[c.discipline]}
                        </div>
                      )}
                      <div className="text-sm text-slate-600 mt-1 line-clamp-2">{c.message}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-7">
          {active ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="contact-detail">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-heading text-2xl font-semibold text-[#0A192F]">{active.name}</div>
                  <a href={`mailto:${active.email}`} className="mt-1 inline-flex items-center gap-2 text-sm text-[#1E3A8A] underline">
                    <Mail size={14} /> {active.email}
                  </a>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setRead(active.id, !active.read)}
                    data-testid="toggle-read"
                    className="px-3 py-2 rounded-full border border-slate-300 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-slate-50"
                  >
                    {active.read ? "Als ungelesen" : "Als gelesen"}
                  </button>
                  <button
                    onClick={() => remove(active.id)}
                    data-testid="delete-contact"
                    className="p-2 rounded-full border border-red-200 text-red-600 hover:bg-red-50"
                    title="Löschen"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                {active.company && (
                  <div className="flex items-start gap-2 text-slate-600">
                    <Building2 size={14} className="mt-0.5 text-slate-400" />
                    {active.company}
                  </div>
                )}
                {active.phone && (
                  <div className="flex items-start gap-2 text-slate-600">
                    <Phone size={14} className="mt-0.5 text-slate-400" />
                    {active.phone}
                  </div>
                )}
                {active.discipline && DISCIPLINE_LABELS[active.discipline] && (
                  <div className="flex items-start gap-2 text-slate-600 col-span-2" data-testid="contact-discipline">
                    <Tag size={14} className="mt-0.5 text-slate-400" />
                    <span>
                      <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1E3A8A] mr-2">Disziplin:</span>
                      {DISCIPLINE_LABELS[active.discipline]}
                    </span>
                  </div>
                )}
                <div className="text-slate-500 col-span-2">
                  Eingegangen am {new Date(active.created_at).toLocaleString("de-DE")}
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-slate-200">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E3A8A] mb-3">Nachricht</div>
                <div className="whitespace-pre-line text-slate-700 leading-relaxed" data-testid="contact-message">
                  {active.message}
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-slate-200">
                <a
                  href={`mailto:${active.email}?subject=Re: Ihre Anfrage an X10 Management`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A192F] text-white rounded-full font-semibold hover:bg-[#1E3A8A]"
                >
                  Antwort verfassen
                </a>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500" data-testid="contact-empty-state">
              Wählen Sie eine Anfrage aus der Liste, um Details zu sehen.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
